import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
  ShieldAlert,
  RefreshCw,
  DollarSign,
  BarChart3,
  Trash2,
  X,
  Sparkles,
  Lock,
  Info,
} from "lucide-react";
import {
  fetchDocuments,
  fetchDocument,
  uploadDocument,
  deleteDocument,
  parseDocument,
  enhanceDocument,
  reclassifyDocument,
  confirmDocument,
  unconfirmDocument,
  type DocumentMeta,
  type ParseFlag,
  type UploadResponse,
} from "../api/client";
import StatusBadge from "../components/StatusBadge";
import LoadingSpinner from "../components/LoadingSpinner";
import { RentRollResultView } from "../components/ParseResultView";
import { OpexResultView, CamRecResultView } from "../components/FinancialParseViews";
import AuditResultView from "../components/AuditResultView";
import { fmtDate, fmtNumber, fmtPercentRaw } from "../utils/format";
import { prettyFieldLabel, prettyFileType } from "../utils/labels";
import { friendlyError } from "../utils/errors";
import ConfirmModal from "../components/ConfirmModal";
import { useToastContext } from "../contexts/ToastContext";
import { usePostParse } from "../contexts/PostParseContext";
import { useAuth } from "../contexts/AuthContext";

type DocType = "rent_roll" | "operating_statement" | "cam_rec";

const DOC_TYPE_META: Record<DocType, { label: string; icon: typeof FileText; emptyDesc: string }> = {
  rent_roll: {
    label: "Rent Roll",
    icon: FileText,
    emptyDesc: "No rent roll uploaded yet",
  },
  operating_statement: {
    label: "Operating Statement",
    icon: DollarSign,
    emptyDesc: "No operating statement uploaded yet",
  },
  cam_rec: {
    label: "CAM Reconciliation",
    icon: BarChart3,
    emptyDesc: "No CAM reconciliation uploaded yet",
  },
};

const DOC_TYPE_ORDER: DocType[] = ["rent_roll", "operating_statement", "cam_rec"];

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function DocumentsPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const toast = useToastContext();
  const [docs, setDocs] = useState<DocumentMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [parsing, setParsing] = useState<string | null>(null);
  const [enhancing, setEnhancing] = useState<string | null>(null);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Upload detection state
  const [lastUpload, setLastUpload] = useState<UploadResponse | null>(null);
  const [showDetected, setShowDetected] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  // Reclassify existing doc state
  const [reclassifyingDocId, setReclassifyingDocId] = useState<string | null>(null);

  // Post-parse modal via global provider
  const { showParseIssues } = usePostParse();

  // Replacement confirmation modal
  const [replaceModal, setReplaceModal] = useState<{
    newDocId: string;
    existingDocId: string;
    fileType: DocType;
    filename: string;
    existingFilename: string;
  } | null>(null);

  // Delete confirmation modal
  const [deleteModal, setDeleteModal] = useState<{
    docId: string;
    filename: string;
    fileType: DocType;
  } | null>(null);

  // Pre-upload type confirmation
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingType, setPendingType] = useState<DocType>("rent_roll");

  const loadDocs = useCallback(() => {
    if (!dealId) return;
    fetchDocuments(dealId)
      .then((d) => { setDocs(d); setError(null); })
      .catch((e) => setError(friendlyError(e instanceof Error ? e.message : "Failed to load documents")))
      .finally(() => setLoading(false));
  }, [dealId]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  // Poll for documents in "enhancing" status
  useEffect(() => {
    const hasEnhancing = docs.some((d) => d.parse_status === "enhancing");
    if (!hasEnhancing) return;
    const interval = setInterval(() => {
      loadDocs();
    }, 5000);
    return () => clearInterval(interval);
  }, [docs, loadDocs]);

  // ----- Upload handler -----

  // ----- Reclassify handler -----

  async function handleReclassify(newType: string) {
    if (!dealId || !lastUpload) return;
    setReclassifying(true);
    try {
      await reclassifyDocument(dealId, lastUpload.id, newType);
      setLastUpload({ ...lastUpload, file_type: newType });
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(`Reclassification failed: ${friendlyError(detail || msg)}`);
    } finally {
      setReclassifying(false);
    }
  }

  async function handleReclassifyExisting(docId: string, newType: string) {
    if (!dealId) return;
    setReclassifyingDocId(docId);
    try {
      await reclassifyDocument(dealId, docId, newType);
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(`Reclassification failed: ${friendlyError(detail || msg)}`);
    } finally {
      setReclassifyingDocId(null);
    }
  }

  async function handleUpload(file: File, overrideType?: DocType) {
    if (!dealId) return;

    // File size validation
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    const WARN_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      setError("File exceeds 50MB limit");
      return;
    }
    if (file.size > WARN_SIZE) {
      setError("Large file — parsing may take longer");
    }

    setUploading(true);
    setShowDetected(false);
    setLastUpload(null);
    try {
      // Upload with user-confirmed type (or let backend auto-detect)
      const uploadResp = await uploadDocument(dealId, file, overrideType);
      setLastUpload(uploadResp);
      setShowDetected(true);
      setUploading(false);
      loadDocs();

      // If there's an existing document of the same type, show replacement modal
      if (uploadResp.existing_doc_id) {
        const existingDoc = docs.find((d) => d.id === uploadResp.existing_doc_id);
        setReplaceModal({
          newDocId: uploadResp.id,
          existingDocId: uploadResp.existing_doc_id,
          fileType: uploadResp.file_type as DocType,
          filename: file.name,
          existingFilename: existingDoc?.filename || "existing document",
        });
        // Don't auto-parse yet — wait for user decision
        return;
      }

      // No conflict — auto-parse immediately
      setParsing(uploadResp.id);
      let parseFailed = false;
      try {
        await parseDocument(dealId, uploadResp.id);
      } catch (parseErr: unknown) {
        parseFailed = true;
        const msg = parseErr instanceof Error ? parseErr.message : "Unknown error";
        const detail = (parseErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setError(`Parse failed for "${file.name}": ${friendlyError(detail || msg)}. The document was uploaded — you can retry parsing from below.`);
      } finally {
        setParsing(null);
      }
      if (!parseFailed) {
        toast.success(`"${file.name}" uploaded and parsed as ${formatFileType(uploadResp.file_type)}`);
        // Check for parse issues to surface in modal
        try {
          const updated = await fetchDocument(dealId, uploadResp.id);
          const issues: Array<{ severity: "critical" | "warning"; message: string; action?: { label: string; path: string } }> = [];
          const pr = updated.parse_result as Record<string, unknown> | null;
          if (pr) {
            const conf = (pr.confidence_score ?? pr.confidence ?? 100) as number;
            if (conf < 50) {
              issues.push({ severity: "critical", message: `Low confidence (${fmtPercentRaw(conf, 0)}). Extracted data may be incomplete.`, action: { label: "Verify fields", path: "verify" } });
            }
            const flags = (pr._validation as Record<string, unknown>)?.flags as unknown[];
            if (Array.isArray(flags) && flags.length > 0) {
              const errorFlags = flags.filter((f: any) => f?.severity === "error");
              if (errorFlags.length > 0) {
                issues.push({ severity: "critical", message: `${errorFlags.length} validation error${errorFlags.length > 1 ? "s" : ""} detected.`, action: { label: "Review on Rent Roll", path: "rent-roll" } });
              }
            }
            // Source coverage stats. The "X% AI-inferred" warning used to
            // fire for every document because every parse path goes through
            // Claude — the signal was meaningless and made clean parses
            // look broken. Keep only the scanned-PDF hint (actionable
            // guidance that source highlighting won't be available).
            const coverage = pr._source_coverage as Record<string, any> | undefined;
            if (coverage?.is_scanned) {
              issues.push({
                severity: "warning",
                message: "This PDF appears to be scanned. Source locations are unavailable. Verify all fields manually.",
                action: { label: "Open Verification", path: "verify" },
              });
            }
          }
          if (issues.length > 0) showParseIssues(dealId, issues);
        } catch {}
      }
      // Clear previous error on successful operation
      setError(null);
      loadDocs();
    } catch (uploadErr: unknown) {
      const msg = uploadErr instanceof Error ? uploadErr.message : "Unknown error";
      const detail = (uploadErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(`Upload failed for "${file.name}": ${friendlyError(detail || msg)}. Check that the backend is running and accessible.`);
      setUploading(false);
    }
  }

  // ----- Replace confirmation handler -----

  async function handleReplaceConfirm() {
    if (!dealId || !replaceModal) return;
    const { newDocId, existingDocId } = replaceModal;
    setReplaceModal(null);

    try {
      // Delete the old document
      await deleteDocument(dealId, existingDocId);
      // Parse the new one
      setParsing(newDocId);
      let parseFailed = false;
      try {
        await parseDocument(dealId, newDocId);
      } catch (parseErr: unknown) {
        parseFailed = true;
        const msg = parseErr instanceof Error ? parseErr.message : "Unknown error";
        const detail = (parseErr as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
        setError(`Parse failed: ${friendlyError(detail || msg)}. You can retry parsing from below.`);
      } finally {
        setParsing(null);
      }
      if (!parseFailed) {
        toast.success("Document replaced and parsed successfully");
      }
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Failed to replace document: ${friendlyError(msg)}`);
      loadDocs();
    }
  }

  async function handleReplaceCancel() {
    if (!dealId || !replaceModal) return;
    const { newDocId } = replaceModal;
    setReplaceModal(null);

    try {
      // Delete the newly uploaded one — keep the old
      await deleteDocument(dealId, newDocId);
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Failed to cancel replacement: ${friendlyError(msg)}`);
      loadDocs();
    }
  }

  // ----- Delete handler -----

  async function handleDeleteConfirm() {
    if (!dealId || !deleteModal) return;
    const { docId } = deleteModal;
    setDeleteModal(null);

    try {
      await deleteDocument(dealId, docId);
      // Collapse if this was expanded
      if (expandedDoc === docId) setExpandedDoc(null);
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Failed to delete document: ${friendlyError(msg)}`);
      loadDocs();
    }
  }

  // ----- Parse handler -----

  async function handleParse(docId: string) {
    if (!dealId) return;
    setParsing(docId);
    try {
      await parseDocument(dealId, docId);
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(`Parse failed: ${friendlyError(detail || msg)}. Check the document format and try again.`);
      loadDocs();
    } finally {
      setParsing(null);
    }
  }

  // ----- Enhance with AI handler -----

  async function handleEnhance(docId: string) {
    if (!dealId) return;
    setEnhancing(docId);
    try {
      await enhanceDocument(dealId, docId);
      loadDocs();
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Enhancement failed: ${friendlyError(detail || msg)}`);
    } finally {
      setEnhancing(null);
    }
  }

  async function handleConfirm(docId: string) {
    if (!dealId) return;
    try {
      await confirmDocument(dealId, docId);
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Confirm failed: ${friendlyError(msg)}`);
    }
  }

  async function handleUnconfirm(docId: string) {
    if (!dealId) return;
    try {
      await unconfirmDocument(dealId, docId);
      loadDocs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setError(`Unconfirm failed: ${friendlyError(msg)}`);
    }
  }

  // ----- Client-side type detection -----

  function detectTypeFromFilename(filename: string): DocType {
    const lower = filename.toLowerCase();
    const opexKw = [
      "income statement", "p&l", "profit and loss", "operating statement",
      "expense report", "budget", "expenses", "opex", "operating expense",
      "financials", "noi", "trailing 12", "t12", "t-12", "detail noi",
      "detail operating", "profit loss", "p+l", "p & l",
    ];
    const rrKw = ["rent roll", "tenant list", "lease schedule", "rent roster"];
    const camKw = [
      "cam reconciliation", "cam rec", "cam recon", "cam summary",
      "reconciliation", "true-up", "trueup", "ye rec", "cam statement",
    ];
    for (const kw of opexKw) { if (lower.includes(kw)) return "operating_statement"; }
    for (const kw of camKw) { if (lower.includes(kw)) return "cam_rec"; }
    for (const kw of rrKw) { if (lower.includes(kw)) return "rent_roll"; }
    return "rent_roll";
  }

  function handleFileSelected(file: File) {
    if (uploading || parsing) {
      toast.error("Please wait for the current upload to finish before adding another file.");
      return;
    }
    const detected = detectTypeFromFilename(file.name);
    setPendingFile(file);
    setPendingType(detected);
  }

  function handlePendingUpload() {
    if (!pendingFile) return;
    const file = pendingFile;
    const type = pendingType;
    setPendingFile(null);
    handleUpload(file, type);
  }

  function cancelPending() {
    setPendingFile(null);
  }

  // ----- Drag & drop -----

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    handleFileSelected(file);
  }

  function onFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFileSelected(file);
    e.target.value = "";
  }

  // Group docs by type
  const docsByType: Record<DocType, DocumentMeta | undefined> = {
    rent_roll: undefined,
    operating_statement: undefined,
    cam_rec: undefined,
  };
  for (const doc of docs) {
    const t = doc.file_type as DocType;
    // Keep the most recent document per type
    if (t in docsByType && !docsByType[t]) {
      docsByType[t] = doc;
    }
  }

  if (loading) return <LoadingSpinner />;

  // Error banner
  const errorBanner = error ? (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-red-700 text-sm font-medium">{error}</p>
      </div>
      <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 text-xs font-medium px-2 py-1 cursor-pointer">
        Dismiss
      </button>
    </div>
  ) : null;

  // Detection banner after upload
  const detectedBanner = showDetected && lastUpload ? (
    <div className="bg-forest-50 border border-forest-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
      <CheckCircle2 className="w-5 h-5 text-forest-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="text-forest-700 text-sm font-medium">
          Detected as <span className="font-bold">{formatFileType(lastUpload.file_type)}</span>
          {lastUpload.auto_detected && (
            <span className="text-forest-500 font-normal"> (auto-detected from content)</span>
          )}
        </p>
        {lastUpload.auto_detected && (
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-forest-600">Wrong type?</span>
            <select
              className="text-xs border border-forest-300 rounded-md px-2 py-1 bg-white text-bark-700 focus:outline-none focus:ring-1 focus:ring-forest-400"
              value={lastUpload.file_type}
              disabled={reclassifying}
              onChange={(e) => handleReclassify(e.target.value)}
            >
              {DOC_TYPE_ORDER.map((dt) => (
                <option key={dt} value={dt}>{DOC_TYPE_META[dt].label}</option>
              ))}
            </select>
            {reclassifying && <Loader2 className="w-3.5 h-3.5 animate-spin text-forest-600" />}
          </div>
        )}
      </div>
      <button onClick={() => setShowDetected(false)} className="text-forest-400 hover:text-forest-600 text-xs font-medium px-2 py-1 cursor-pointer">
        Dismiss
      </button>
    </div>
  ) : null;

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      {/* Page header */}
      <div>
        <h2 className="font-display text-lg sm:text-xl font-semibold text-bark-800">Documents</h2>
        <p className="text-xs sm:text-sm text-bark-400 mt-1">
          Upload rent rolls, operating statements, and CAM reconciliations — type is detected automatically
        </p>
      </div>

      {/* Error banner */}
      {errorBanner}

      {/* Detection banner */}
      {detectedBanner}

      {/* Single Upload Zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-xl p-6 sm:p-10 text-center transition-all duration-200 ${
          dragOver
            ? "border-honey-500 bg-honey-50"
            : "border-sage-300 bg-white hover:border-forest-400 hover:bg-forest-50"
        }`}
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-3 text-honey-700">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-sm font-medium">Uploading and detecting type...</span>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-honey-100 border border-honey-200 flex items-center justify-center mx-auto mb-2 sm:mb-3">
              <Upload className="w-5 h-5 sm:w-6 sm:h-6 text-honey-600" />
            </div>
            <p className="text-sm sm:text-base font-medium text-bark-700 mb-1">
              <span className="hidden sm:inline">Drop document here or </span>
              <label className="text-honey-600 hover:text-honey-500 cursor-pointer font-semibold underline underline-offset-2 transition-colors">
                <span className="sm:hidden">Tap to upload</span>
                <span className="hidden sm:inline">click to browse</span>
                <input type="file" accept=".pdf,.xlsx,.xls" onChange={onFileSelect} className="hidden" />
              </label>
            </p>
            <p className="text-xs sm:text-sm text-bark-400">
              PDF, XLSX, XLS — document type is auto-detected
            </p>
          </>
        )}
      </div>

      {/* Pending file confirmation */}
      {pendingFile && (
        <div className="bg-white border border-honey-300 rounded-xl p-4 shadow-sm animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-honey-100 border border-honey-200 flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5 text-honey-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-bark-700 truncate">{pendingFile.name}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-bark-500">Type:</span>
                <select
                  value={pendingType}
                  onChange={(e) => setPendingType(e.target.value as DocType)}
                  className="text-xs border border-grove-300 rounded-md px-2 py-1 bg-white text-bark-700 focus:outline-none focus:ring-1 focus:ring-forest-400"
                >
                  {DOC_TYPE_ORDER.map((dt) => (
                    <option key={dt} value={dt}>{DOC_TYPE_META[dt].label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={cancelPending}
                className="px-3 py-1.5 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePendingUpload}
                className="px-4 py-1.5 text-xs font-medium text-white bg-forest-700 rounded-lg hover:bg-forest-800 transition-colors"
              >
                Upload &amp; Parse
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Inventory */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
          Document Inventory
        </h3>
        <div className="space-y-3 sm:space-y-4">
          {DOC_TYPE_ORDER.map((docType) => {
            const meta = DOC_TYPE_META[docType];
            const doc = docsByType[docType];

            return (
              <DocumentCard
                key={docType}
                docType={docType}
                meta={meta}
                doc={doc}
                dealId={dealId || ""}
                isExpanded={doc ? expandedDoc === doc.id : false}
                isParsing={doc ? parsing === doc.id : false}
                isEnhancing={doc ? enhancing === doc.id : false}
                onToggleExpand={() => {
                  if (doc) setExpandedDoc(expandedDoc === doc.id ? null : doc.id);
                }}
                onParse={() => {
                  if (doc) handleParse(doc.id);
                }}
                onDelete={() => {
                  if (doc) {
                    setDeleteModal({
                      docId: doc.id,
                      filename: doc.filename,
                      fileType: docType,
                    });
                  }
                }}
                onEnhance={(doc?.parse_status === "complete" || doc?.parse_status === "needs_review") ? () => handleEnhance(doc.id) : undefined}
                isReclassifying={doc ? reclassifyingDocId === doc.id : false}
                onReclassify={doc ? (newType) => handleReclassifyExisting(doc.id, newType) : undefined}
                onConfirm={(doc?.parse_status === "complete" || doc?.parse_status === "needs_review") && !doc.confirmed_at ? () => handleConfirm(doc.id) : undefined}
                onUnconfirm={doc?.confirmed_at ? () => handleUnconfirm(doc.id) : undefined}
              />
            );
          })}
        </div>
      </div>

      {/* Replace modal */}
      {replaceModal && (
        <ConfirmModal
          title="Replace Existing Document?"
          message={`Replace existing document '${replaceModal.existingFilename}' with '${replaceModal.filename}'?`}
          confirmLabel="Replace"
          cancelLabel="Cancel"
          onConfirm={handleReplaceConfirm}
          onCancel={handleReplaceCancel}
          danger
        />
      )}

      {/* Delete modal */}
      {deleteModal && (
        <ConfirmModal
          title="Remove Document?"
          message={`Are you sure you want to remove "${deleteModal.filename}" (${formatFileType(deleteModal.fileType)})? This action cannot be undone.`}
          confirmLabel="Remove"
          cancelLabel="Keep"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteModal(null)}
          danger
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Document Card
// ---------------------------------------------------------------------------

interface DocumentCardProps {
  docType: DocType;
  meta: { label: string; icon: typeof FileText; emptyDesc: string };
  doc: DocumentMeta | undefined;
  dealId: string;
  isExpanded: boolean;
  isParsing: boolean;
  isEnhancing?: boolean;
  isReclassifying?: boolean;
  onToggleExpand: () => void;
  onParse: () => void;
  onDelete: () => void;
  onEnhance?: () => void;
  onReclassify?: (newType: string) => void;
  onConfirm?: () => void;
  onUnconfirm?: () => void;
}

function DocumentCard({
  docType,
  meta,
  doc,
  dealId,
  isExpanded,
  isParsing,
  isEnhancing,
  isReclassifying,
  onToggleExpand,
  onParse,
  onDelete,
  onEnhance,
  onReclassify,
  onConfirm,
  onUnconfirm,
}: DocumentCardProps) {
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  const Icon = meta.icon;
  const colorMap: Record<DocType, { bg: string; border: string; icon: string; emptyBg: string }> = {
    rent_roll: {
      bg: "bg-forest-50",
      border: "border-forest-200",
      icon: "text-forest-600",
      emptyBg: "bg-grove-50",
    },
    operating_statement: {
      bg: "bg-honey-50",
      border: "border-honey-200",
      icon: "text-honey-600",
      emptyBg: "bg-grove-50",
    },
    cam_rec: {
      bg: "bg-blue-50",
      border: "border-blue-200",
      icon: "text-blue-600",
      emptyBg: "bg-grove-50",
    },
  };
  const colors = colorMap[docType];

  if (!doc) {
    // Empty state
    return (
      <div className={`rounded-xl border border-grove-300 ${colors.emptyBg} px-4 sm:px-6 py-5 sm:py-6`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colors.bg} ${colors.border}`}>
            <Icon className={`w-4 h-4 ${colors.icon}`} />
          </div>
          <div>
            <p className="text-sm font-medium text-bark-700">{meta.label}</p>
            <p className="text-xs text-bark-400 mt-0.5">{meta.emptyDesc}</p>
          </div>
        </div>
      </div>
    );
  }

  const flags = extractFlags(doc);
  const hasWarnings = flags.some((f) => f.severity === "warning");
  const hasErrors = flags.some((f) => f.severity === "error");
  const confidenceScore = doc.parse_result?.confidence_score as number | undefined;

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 sm:py-5">
        {/* Header row */}
        <div className="flex items-start sm:items-center gap-3 sm:gap-4">
          <div className="flex-shrink-0">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colors.bg} ${colors.border}`}>
              <Icon className={`w-4 h-4 ${colors.icon}`} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400">{meta.label}</p>
              <StatusBadge status={doc.parse_status} />
              {hasErrors && <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />}
              {hasWarnings && !hasErrors && <AlertTriangle className="w-4 h-4 text-honey-600 flex-shrink-0" />}
            </div>
            <p className="text-sm font-medium text-bark-700 truncate mt-0.5">{doc.filename}</p>
            <p className="text-xs text-bark-400 mt-0.5">
              Uploaded {fmtDate(doc.created_at)}
            </p>
            {doc.parse_status === "error" && doc.parse_error && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Parse failed: </span>
                  {friendlyParseError(doc.parse_error, doc.file_type)}
                </div>
              </div>
            )}
            {doc.parse_status === "needs_review" && doc.parse_error && (
              <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-honey-50 border border-honey-200 rounded-lg text-xs text-honey-800">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="font-medium">Needs review: </span>
                  {doc.parse_error}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {confidenceScore != null && (doc.parse_status === "complete" || doc.parse_status === "needs_review") && (
              <ConfidenceBadge score={confidenceScore} />
            )}
            {(doc.parse_status === "complete" || doc.parse_status === "needs_review") && (() => {
              const val = doc.parse_result?._validation as { summary?: { errors?: number; warnings?: number } } | undefined;
              if (!val?.summary) return null;
              const { errors = 0, warnings = 0 } = val.summary;
              if (errors === 0 && warnings === 0) return null;
              return (
                <span
                  className={`flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md border ${
                    errors > 0
                      ? "bg-red-50 text-red-700 border-red-200"
                      : "bg-honey-50 text-honey-700 border-honey-200"
                  }`}
                  title={`${errors} error${errors !== 1 ? "s" : ""}, ${warnings} warning${warnings !== 1 ? "s" : ""} — click Rent Roll tab to review`}
                >
                  <AlertTriangle className="w-3 h-3" />
                  {errors > 0 && `${errors}E`}
                  {errors > 0 && warnings > 0 && " "}
                  {warnings > 0 && `${warnings}W`}
                </span>
              );
            })()}
            {doc.audit_result && !doc.audit_result.error && (
              <span className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-forest-50 text-forest-700 border border-forest-200 rounded-md" title="AI audit complete">
                <CheckCircle2 className="w-3 h-3" />
                Audited
              </span>
            )}
            <button
              onClick={onDelete}
              className="p-2 text-bark-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
              title="Remove document"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3 sm:mt-2 sm:ml-14">
          {doc.parse_status === "pending" && (
            <button onClick={onParse} disabled={isParsing}
              className="flex-1 sm:flex-none px-4 py-2 text-xs font-semibold bg-honey-600 text-white rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all shadow-sm hover:shadow-md">
              {isParsing ? "Parsing..." : "Parse"}
            </button>
          )}
          {(doc.parse_status === "complete" || doc.parse_status === "enhancing" || doc.parse_status === "error") && (
            <button onClick={onParse} disabled={isParsing}
              className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-3 py-2 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all disabled:opacity-50">
              {isParsing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              {isParsing ? "Parsing..." : "Re-parse"}
            </button>
          )}
          {onReclassify && (doc.parse_status === "complete" || doc.parse_status === "error") && (
            <div className="flex items-center gap-1.5">
              <select
                className="text-xs border border-grove-300 rounded-lg px-2 py-2 bg-white text-bark-600 focus:outline-none focus:ring-1 focus:ring-forest-400 cursor-pointer"
                value={docType}
                disabled={isReclassifying}
                onChange={(e) => onReclassify(e.target.value)}
              >
                {DOC_TYPE_ORDER.map((dt) => (
                  <option key={dt} value={dt}>{DOC_TYPE_META[dt].label}</option>
                ))}
              </select>
              {isReclassifying && <Loader2 className="w-3.5 h-3.5 animate-spin text-bark-400" />}
            </div>
          )}
          {doc.parse_status === "processing" && (
            <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-honey-700">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Processing...
            </div>
          )}
          {doc.parse_status === "enhancing" && (
            <div className="flex items-center gap-1.5 px-3 py-2 text-xs text-honey-600">
              <Sparkles className="w-3.5 h-3.5 animate-pulse" />
              AI enhancement in progress...
            </div>
          )}
          {doc.parse_status === "complete" && (
            <>
              {onEnhance && confidenceScore != null && confidenceScore < 100 && (
                <button onClick={onEnhance} disabled={isEnhancing}
                  className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-3 py-2 text-xs font-semibold text-white bg-forest-600 rounded-lg hover:bg-forest-500 hover:shadow-md transition-all disabled:opacity-50 shadow-sm">
                  {isEnhancing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {isEnhancing ? "Enhancing..." : "Enhance with AI"}
                </button>
              )}
              {(() => {
                const enh = doc.parse_result?._enhancement as Record<string, unknown> | undefined;
                return enh?.before_score != null ? (
                  <span className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium bg-forest-50 text-forest-700 border border-forest-200 rounded-md">
                    {String(enh.before_score)} → {String(enh.after_score)}
                  </span>
                ) : null;
              })()}
              {onUnconfirm && doc.confirmed_at && (
                <button onClick={onUnconfirm}
                  className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-3 py-2 text-xs font-medium text-forest-700 bg-forest-50 border border-forest-300 rounded-lg hover:bg-white hover:shadow-sm transition-all"
                  title={`Confirmed ${fmtDate(doc.confirmed_at)}`}>
                  <Lock className="w-3.5 h-3.5" />
                  Locked
                </button>
              )}
              {onConfirm && !doc.confirmed_at && (
                <button onClick={onConfirm}
                  className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-3 py-2 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-forest-50 hover:text-forest-700 hover:shadow-sm transition-all">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Confirm
                </button>
              )}
              <button onClick={onToggleExpand}
                className="flex items-center justify-center gap-1.5 flex-1 sm:flex-none px-3 py-2 text-xs font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all">
                {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                Details
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded parse results */}
      {isExpanded && doc.parse_result && (
        <div className="px-4 sm:px-6 pb-4 sm:pb-5 border-t border-grove-200 bg-grove-50">
          {isAdmin && <ParseAuditInfo parseResult={doc.parse_result} />}
          <ValidationFlagsSection parseResult={doc.parse_result} />
          {doc.file_type === "rent_roll" && <RentRollResultView result={doc.parse_result} dealId={dealId} />}
          {doc.file_type === "operating_statement" && <OpexResultView result={doc.parse_result} />}
          {doc.file_type === "cam_rec" && <CamRecResultView result={doc.parse_result} />}
          {doc.audit_result && <AuditResultView audit={doc.audit_result} />}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Confidence Badge
// ---------------------------------------------------------------------------

function ConfidenceBadge({ score }: { score: number }) {
  let color: string;
  let Icon: typeof ShieldCheck;
  let label: string;

  if (score >= 85) {
    color = "bg-forest-50 text-forest-700 border-forest-200";
    Icon = ShieldCheck;
    label = "High";
  } else if (score >= 60) {
    color = "bg-honey-50 text-honey-700 border-honey-200";
    Icon = ShieldCheck;
    label = "Medium";
  } else if (score >= 35) {
    color = "bg-orange-50 text-orange-700 border-orange-200";
    Icon = ShieldAlert;
    label = "Low";
  } else {
    color = "bg-red-50 text-red-700 border-red-200";
    Icon = ShieldAlert;
    label = "Review";
  }

  return (
    <div className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border text-[10px] sm:text-xs font-medium ${color}`}>
      <Icon className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
      <span>{score}</span>
      <span className="text-[9px] sm:text-[10px] opacity-70 hidden sm:inline">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Parse Audit Info
// ---------------------------------------------------------------------------

function ParseAuditInfo({ parseResult }: { parseResult: Record<string, unknown> }) {
  const enh = parseResult._enhancement as Record<string, unknown> | undefined;
  const usage = parseResult._usage as { input_tokens?: number; output_tokens?: number; max_tokens?: number; possibly_truncated?: boolean } | undefined;
  const trunc = parseResult._truncation as { truncated?: boolean; total_pages?: number; pages_sent?: number } | undefined;
  const method = parseResult.extraction_method as string | undefined;

  if (!enh && !usage && !trunc && !method) return null;

  const methodLabel = (m: string | undefined) => {
    if (!m) return "Unknown";
    if (m.includes("ai_enhanced")) return "AI Enhanced";
    if (m.includes("ai_single_pass") || m.includes("ai_full_reextract")) return "AI Extraction";
    if (m.includes("ai")) return "AI";
    if (m.includes("deterministic_ocr")) return "OCR + Deterministic";
    if (m.includes("deterministic")) return "Deterministic";
    return prettyFieldLabel(m);
  };

  const usedLabel = enh?.used as string | undefined;
  const reason = enh?.reason as string | undefined;
  const detScore = enh?.deterministic_score as number | undefined;
  const aiScore = (enh?.ai_score ?? enh?.enhanced_score) as number | undefined;

  return (
    <div className="mt-3 mb-2 p-3 bg-white rounded-lg border border-grove-200">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-bark-600 mb-2">
        <Info className="w-3.5 h-3.5 text-bark-400" />
        Parse Details
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-bark-500">
        <div>
          <span className="text-bark-400">Method:</span>{" "}
          <span className="font-medium text-bark-700">{methodLabel(method || usedLabel)}</span>
        </div>
        {detScore != null && (
          <div>
            <span className="text-bark-400">Det. Score:</span>{" "}
            <span className="font-medium text-bark-700">{detScore}</span>
          </div>
        )}
        {aiScore != null && (
          <div>
            <span className="text-bark-400">AI Score:</span>{" "}
            <span className="font-medium text-bark-700">{aiScore}</span>
          </div>
        )}
        {reason && (
          <div>
            <span className="text-bark-400">Decision:</span>{" "}
            <span className="font-medium text-bark-700">{prettyFieldLabel(reason)}</span>
          </div>
        )}
        {usage?.input_tokens != null && (
          <div>
            <span className="text-bark-400">Tokens:</span>{" "}
            <span className="font-medium text-bark-700">{fmtNumber(usage.input_tokens + (usage.output_tokens ?? 0))}</span>
          </div>
        )}
      </div>
      {trunc?.truncated && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-orange-600">
          <AlertTriangle className="w-3 h-3" />
          PDF truncated: {trunc.pages_sent} of {trunc.total_pages} pages sent to AI
        </div>
      )}
      {usage?.possibly_truncated && (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-orange-600">
          <AlertTriangle className="w-3 h-3" />
          AI response may have been truncated (hit token limit)
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation Flags Section (collapsible)
// ---------------------------------------------------------------------------

function ValidationFlagsSection({ parseResult }: { parseResult: Record<string, unknown> }) {
  const [open, setOpen] = useState(false);

  const val = parseResult._validation as {
    flags?: Array<{
      severity: string;
      field: string;
      tenant?: string | null;
      message: string;
    }>;
    summary?: { errors?: number; warnings?: number };
  } | undefined;

  const flags = val?.flags;
  if (!flags || flags.length === 0) return null;

  const errorCount = val?.summary?.errors ?? flags.filter((f) => f.severity === "error").length;
  const warningCount = val?.summary?.warnings ?? flags.filter((f) => f.severity === "warning").length;

  return (
    <div className="mt-3 mb-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs font-semibold text-bark-600 hover:text-bark-800 transition-colors"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <AlertTriangle className={`w-3.5 h-3.5 ${errorCount > 0 ? "text-red-500" : "text-honey-600"}`} />
        <span>
          Validation Issues
          <span className="ml-1.5 font-normal text-bark-400">
            ({errorCount > 0 && `${errorCount} error${errorCount !== 1 ? "s" : ""}`}
            {errorCount > 0 && warningCount > 0 && ", "}
            {warningCount > 0 && `${warningCount} warning${warningCount !== 1 ? "s" : ""}`})
          </span>
        </span>
      </button>
      {open && (
        <div className="mt-2 space-y-1.5 ml-5 max-h-64 overflow-y-auto">
          {flags.map((flag, i) => (
            <div
              key={i}
              className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs border ${
                flag.severity === "error"
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-honey-50 border-honey-200 text-honey-700"
              }`}
            >
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">{fieldLabel(flag.field)}</span>
                {flag.tenant && (
                  <span className="text-[10px] opacity-70 ml-1">({flag.tenant})</span>
                )}
                <span className="mx-1">&mdash;</span>
                <span>{flag.message}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert snake_case parser field names using the label registry. */
const fieldLabel = prettyFieldLabel;
const formatFileType = prettyFileType;

function extractFlags(doc: DocumentMeta): ParseFlag[] {
  if (!doc.parse_result) return [];
  return (doc.parse_result as unknown as { flags?: ParseFlag[] }).flags ?? [];
}

function friendlyParseError(error: string, fileType: string): string {
  if (error.includes("No text could be extracted"))
    return `This ${formatFileType(fileType)} looks like a scanned image. Try uploading a version with selectable text, or contact support so we can run image extraction for you.`;
  if (error.includes("No tables found"))
    return `We couldn't find data tables in this ${formatFileType(fileType)}. The format may not be supported — try a different file or reach out for help.`;
  return error;
}

